import { defineStore } from "pinia";
import { fetchData, patchData, putData } from "../libs/axios";

const BUSINESS_PRIVATE_API_URL = "/api/activity/v1/org/business";
const BOOKING_SETTINGS_STORAGE_KEY = "bookingSettingsData";

const localStorageDataIsValidObject = (storageData) => storageData
        && Object.keys(storageData).length !== 0
        && storageData.constructor === Object;

export const useBookingSettingsStore = defineStore("bookingSettings", {
    state: () => ({
        businessId: null,
        isLoaded: false,
        shouldReload: false,
        isFirstActivity: undefined,
        refundPolicy: "",
        termsConditions: "",
        resalePolicy: "",
        privacyPolicy: "",
        immutablePolicies: {},
        errors: {},
    }),
    actions: {
        async getLocalStorage(storage) {
            if (storage) {
                const storageData = await storage?.getStorageSync(BOOKING_SETTINGS_STORAGE_KEY);
                if (localStorageDataIsValidObject(storageData)) {
                    this.$state = storageData;
                    return true;
                }
                return false;
            }

            const localStorageData = localStorage.getItem(BOOKING_SETTINGS_STORAGE_KEY);
            const parsedLocalStorageData = JSON.parse(localStorageData);
            if (localStorageDataIsValidObject(parsedLocalStorageData)) {
                this.$state = parsedLocalStorageData;
                return true;
            }
            return false;
        },

        async setLocalStorage(storage) {
            if (storage) {
                await storage?.setStorageSync(BOOKING_SETTINGS_STORAGE_KEY, this.$state);
                return;
            }
            const jsonStringifyState = JSON.stringify(this.$state);
            localStorage.setItem(BOOKING_SETTINGS_STORAGE_KEY, jsonStringifyState);
        },

        async removeLocalStorage(storage) {
            if (storage) {
                await storage?.removeStorageSync(BOOKING_SETTINGS_STORAGE_KEY);
            }
            localStorage.removeItem(BOOKING_SETTINGS_STORAGE_KEY);
        },

        setImmutablePolicies() {
            this.$state.immutablePolicies = {
                refundPolicy: this.$state.refundPolicy,
                termsConditions: this.$state.termsConditions,
                resalePolicy: this.$state.resalePolicy,
                privacyPolicy: this.$state.privacyPolicy,
            };
        },

        validate() {
            this.$state.errors = {};
            // Validate refund policy
            if (this.$state.refundPolicy === "") {
                this.$state.errors.refundPolicy = "You must enter a refund policy.";
            }

            if (this.$state.refundPolicy.length > 10000
                || this.$state.refundPolicy.length < 10) {
                this.$state.errors.refundPolicy = "Refund policy must be between 10 and 10000 characters.";
            }
            // Validate terms and conditions
            if (this.$state.termsConditions === "") {
                this.$state.errors.termsConditions = "You must enter terms and conditions.";
            }

            if (this.$state.termsConditions.length > 10000
                || this.$state.termsConditions.length < 10) {
                this.$state.errors.termsConditions = "Terms and conditions must be between 10 and 10000 characters.";
            }
            // Validate resale policy
            if (this.$state.resalePolicy === "") {
                this.$state.errors.resalePolicy = "You must enter a resale policy.";
            }

            if (this.$state.resalePolicy.length > 10000
                || this.$state.resalePolicy.length < 10) {
                this.$state.errors.resalePolicy = "Resale policy must be between 10 and 10000 characters.";
            }
            // Validate privacy policy
            if (this.$state.privacyPolicy === "") {
                this.$state.errors.privacyPolicy = "You must enter a privacy policy.";
            }

            if (this.$state.privacyPolicy.length > 10000 || this.$state.privacyPolicy.length < 10) {
                this.$state.errors.privacyPolicy = "Privacy policy must be between 10 and 10000 characters.";
            }
            console.log(this.$state.errors);
            // Return true if no errors
            return Object.keys(this.$state.errors).length === 0;
        },

        async getBookingSettings() {
            if (this.$state.isLoaded
                && !this.$state.shouldReload
                && this.$state.isFirstActivity !== undefined) {
                return;
            }

            if (!this.$state.businessId) {
                console.error("Business ID is not set.");
                return;
            }

            try {
                const data = await fetchData(`${BUSINESS_PRIVATE_API_URL}/${this.$state.businessId}/policy`);
                this.$state.refundPolicy = data.result.policyRefund?.body;
                this.$state.termsConditions = data.result.policyTerms?.body;
                this.$state.resalePolicy = data.result.policyResale?.body;
                this.$state.privacyPolicy = data.result.policyPrivacy?.body;
                this.$state.isFirstActivity = this.$state.refundPolicy === "" && this.$state.termsConditions === "" && this.$state.resalePolicy === "" && this.$state.privacyPolicy === "";
                this.$state.isLoaded = true;
                this.$state.shouldReload = false;
                this.setImmutablePolicies();
            } catch (error) {
                this.$state.errors.bookingSettingsApiError = "Error loading booking settings.";
                console.error("Booking setting loading error:", error);
            }
        },

        async getSetBookingSettingsWithStorage(storage) {
            if (this.$state.shouldReload || this.$state.isFirstActivity === undefined) {
                await this.removeLocalStorage(storage);
            }
            const storedAtStorage = await this.getLocalStorage(storage);
            if (storedAtStorage) {
                return;
            }
            await this.getBookingSettings();
            await this.setLocalStorage(storage);
        },

        async saveBookingSettings() {
            if (!this.validate() || !this.$state.businessId || !this.$state.isFirstActivity) {
                return;
            }

            const savingData = {
                policyRefund: this.$state.refundPolicy,
                policyTerms: this.$state.termsConditions,
                policyResale: this.$state.resalePolicy,
                policyPrivacy: this.$state.privacyPolicy,
            };

            try {
                await putData(`${BUSINESS_PRIVATE_API_URL}/${this.$state.businessId}/policy`, savingData);
                this.$state.shouldReload = true;
            } catch (error) {
                this.$state.errors.bookingSettingsApiError = "Error saving booking settings.";
                console.error("Booking settings saving error:", error);
                throw error;
            }
        },

        async updateBookingSettings(data) {
            const changedData = {};
            if (data.refundPolicy !== this.$state.refundPolicy) {
                changedData.policyRefund = data.refundPolicy;
            }
            if (data.termsConditions !== this.$state.termsConditions) {
                changedData.policyTerms = data.termsConditions;
            }
            if (data.resalePolicy !== this.$state.resalePolicy) {
                changedData.policyResale = data.resalePolicy;
            }
            if (data.privacyPolicy !== this.$state.privacyPolicy) {
                changedData.policyPrivacy = data.privacyPolicy;
            }
            // If no changes, return
            if (Object.keys(changedData).length === 0) {
                return;
            }

            try {
                await patchData(`${BUSINESS_PRIVATE_API_URL}/${this.$state.businessId}/policy`, changedData);
                this.$state.shouldReload = true;
            } catch (error) {
                this.$state.errors.bookingSettingsApiError = "Error saving booking settings.";
                console.error("Booking settings saving error:", error);
                throw error;
            }
        },

        async reset(storage = null) {
            this.$state.businessId = null;
            this.$state.isFirstActivity = undefined;
            this.$state.refundPolicy = "";
            this.$state.termsConditions = "";
            this.$state.resalePolicy = "";
            this.$state.privacyPolicy = "";
            this.$state.immutablePolicies = {};
            this.$state.errors = {};

            if (storage) {
                await this.removeLocalStorage(storage);
            }
        },
    },
    getters: {
        isBookingSettingsChanged: (state) => ({
            isFirstActivity: state.isFirstActivity,
            isRefundPolicyChanged: state.refundPolicy !== state.immutablePolicies.refundPolicy,
            isTermsConditionsChanged: state.termsConditions
                !== state.immutablePolicies.termsConditions,
            isResalePolicyChanged: state.resalePolicy !== state.immutablePolicies.resalePolicy,
            isPrivacyPolicyChanged: state.privacyPolicy !== state.immutablePolicies.privacyPolicy,
        }),
    },
});

export default useBookingSettingsStore;
